import {
  type FacePackerPolygon,
  type PackedFacePanel,
} from '@payaca/components/solarRoofView/SolarRoofView';
import {
  type SolarRoofPlane,
  type SolarMounting,
} from '@payaca/helpers/solarMounting';
import { type SolarGenerationProvenance } from '@payaca/helpers/solarGenerationAvailability';
import { type SolarPlacementRules } from '@payaca/helpers/solarPlacementRules';
import { type SolarPlaneFit } from '@payaca/helpers/solarPlaneFit';
import { type SolarRoofEdgeAnchor } from '@payaca/helpers/solarRoofEdgeAnchor';

/** A panel in the designer's live working layout. `faceId` is stable across reindexing; `faceIndex` is not. */
export type EditPanel = PackedFacePanel & {
  id?: string;
  equipmentSelectionId?: string;
  placementGroupId?: string;
  included: boolean;
  faceId: number;
  mcsYearlyEnergyKwh?: number;
};

/** Per-face sun access computed server-side from the Google hourlyShade rasters. Communication only — the savings maths never reads it. */
export type FaceShadingView = {
  annualSunAccessPercent: number;
  /** Jan..Dec. */
  monthlySunAccessPercent: number[];
  /** 12 months × 24 UTC hours, % of days with sun at that hour (absolute). */
  hourlySunAccessPercent: number[][];
};

/** Where a face's pitch came from: 'dsm' = measured, 'manual' = installer-declared, 'assumed' = neither. */
export type FacePitchSource = 'dsm' | 'assumed' | 'manual';

export type WorkingFace = {
  planeFit?: SolarPlaneFit;
  id: number;
  polygon: FacePackerPolygon;
  pitchDegrees: number;
  azimuthDegrees: number;
  roofPlane?: SolarRoofPlane;
  mounting?: SolarMounting;
  arrayAzimuthDegrees?: number;
  appliedMounting?: 'flush' | 'tilt';
  appliedPlacementRules?: SolarPlacementRules;
  roofEdgeAnchor?: SolarRoofEdgeAnchor | null;
  appliedRoofEdgeAnchor?: SolarRoofEdgeAnchor | null;
  /** Null records that entering reference mode could not establish applied axes. */
  appliedArrayAzimuthDegrees?: number | null;
  generation?: SolarGenerationProvenance;
  /** "Include anyway": every repack packs this roof whatever the skip rules say. */
  rulesOverride?: true;
  segmentIndex: number;
  areaMeters2: number;
  isNew: boolean;
  pitchSource: FacePitchSource;
  /** Faces dropped together by one quick-roof template share a group id (dragging one slides the group); null otherwise. */
  groupId: number | null;
  /** Generate-time sun access for the ORIGINAL traced face — stale after a reshape; absent on installer-added faces. */
  shading?: FaceShadingView | null;
  /** Generate-time verdict from the vision pass: does the photo show roof across this face? partly/no = confirm on survey. Absent on installer-added faces. */
  photoAgreement?: { agrees: string; note?: string | null } | null;
};

/**
 * The designer's active tool (geometry mode): select, draw, obstacle, move,
 * trace (seed a face from an OSM outline), pin (reposition the analysis
 * point).
 */
export type DesignerTool =
  | 'select'
  | 'draw'
  | 'obstacle'
  | 'move'
  | 'trace'
  | 'pin';

export const roofModelProviderLabel = (
  provider: 'GOOGLE' | 'YOURPOWER' | null | undefined
): string | null => {
  if (provider === 'YOURPOWER') return 'YourPower roof model';
  if (provider === 'GOOGLE') return 'Google roof model';
  return null;
};

export type FaceSummary = {
  faceId: number;
  /** The physical roof's, even when its panels sit on tilt frames. */
  azimuthDegrees: number;
  pitchDegrees: number;
  /** Sloping roof surface (plan area ÷ cos roof pitch). */
  areaMeters2: number;
  /** Panels the layout placed on the face, included or not. */
  panelsAvailable: number;
  panelsIncluded: number;
  /** Sum of the included panels' yearly yield. */
  annualKwh: number | null;
  /** Included panels × panel capacity, in kWp; null without a capacity. */
  systemKwp: number | null;
  annualSunAccessPercent: number | null;
  /** See WorkingFace.photoAgreement. */
  photoAgreement: { agrees: string; note?: string | null } | null;
  /** "Include anyway": packed whatever the Array rules say. */
  rulesOverride?: boolean;
  planeFit?: Pick<SolarPlaneFit, 'status'>;
};

/** First id handed to installer-added faces — clear of original 0..n-1 ids. */
export const FIRST_ADDED_FACE_ID = 100_000;
/** Fallback pitch for an added face when no existing face suggests one. */
export const DEFAULT_ADDED_FACE_PITCH_DEGREES = 30;
